import * as commonUtils from '@Common/Helpers/CommonUtils';
import { BaseExpressionElement, FieldOperand, ParameterOperand, RFunctionOperand, StoredConstant } from '@InstanceFactory';
import { StatementVisitorBase } from '@Studio/StatementVisitorBase';
import { enumerable } from '@Common/Core/CoreDecorator';

export function getMetricOperandName(expression, metricName): string {
    const metricsInStatementCollector = new MetricsInStatementCollector();

    metricsInStatementCollector.accept(expression);

    const metricNames = _.map(metricsInStatementCollector.metricOperands, metricOperand => metricOperand.name);
    return commonUtils.getDefaultName(metricName, metricNames, formatMetricOperandName);
}

export function setMetricOperandNames(expression): void {
    const metricsInStatementCollector = new MetricsInStatementCollector();

    metricsInStatementCollector.accept(expression);

    const metricNames = [];
    _.forEach(metricsInStatementCollector.metricOperands, metricOperand => {
        metricOperand.name = commonUtils.getDefaultName(metricOperand.metric.name, metricNames, formatMetricOperandName);
        metricNames.push(metricOperand.name);
    });
}

export function formatMetricOperandName(prefix, index): any {
    return index == 0 ? prefix : prefix + '(' + index + ')';
}

export class MetricsInStatementCollector extends StatementVisitorBase {
    private _metricOperands: any[] = [];

    @enumerable
    public get metricOperands(): any[] {
        return this._metricOperands;
    }

    protected visitMetric(metric): void {
        this._metricOperands.push(metric);
    }
}

export class RFunctionsInStatementCollector extends StatementVisitorBase {
    public rFunctionOperands: { rFunction: RFunctionOperand; parent: BaseExpressionElement }[] = [];
    private partsStack: BaseExpressionElement[] = [];

    public accept(statementPart): void {
        this.partsStack.push(statementPart);
        super.accept(statementPart);
        this.partsStack.pop();
    }

    protected visitRFunction(rFunction: RFunctionOperand): void {
        const parent = this.partsStack[this.partsStack.length - 2];
        this.rFunctionOperands.push({
            rFunction: rFunction,
            parent: parent
        });
    }
}

export class ParametersAndConstantsInStatementCollector extends StatementVisitorBase {
    private _parameters: ParameterOperand[] = [];

    @enumerable
    public get parameters(): ParameterOperand[] {
        return this._parameters;
    }

    private _constants: StoredConstant[] = [];

    @enumerable
    public get constants(): StoredConstant[] {
        return this._constants;
    }

    protected visitQueryParameter(parameter: ParameterOperand): void {
        this._parameters.push(parameter);
    }

    protected visitStoredConstant(storedConstant: StoredConstant): void {
        this._constants.push(storedConstant);
    }
}

export class ReplaceAliasInStatement extends StatementVisitorBase {
    private _oldAlias: string;
    private _newAlias: string;

    constructor(oldAlias: string, newAlias: string) {
        super();
        this._oldAlias = oldAlias;
        this._newAlias = newAlias;
    }

    protected visitField(field): void {
        if (field.tableAlias === this._oldAlias) {
            field.tableAlias = this._newAlias;
            field.representation = field.name = field.tableAlias + '.' + field.fieldId;
        }
    }
}

export class ReplaceFieldIdInStatement extends StatementVisitorBase {
    private _oldFieldId: string;
    private _newFieldId: string;
    private _tableAlias: string;

    constructor(oldFieldId: string, newFieldId: string, tableAlias: string) {
        super();
        this._oldFieldId = oldFieldId;
        this._newFieldId = newFieldId;
        this._tableAlias = tableAlias;
    }

    protected visitField(field): void {
        if (field.tableAlias === this._tableAlias && field.fieldId === this._oldFieldId) {
            field.fieldId = this._newFieldId;
            field.name = field.tableAlias + '.' + this._newFieldId;
        }
    }
}

export class FieldsInStatementCollector extends StatementVisitorBase {
    private fields: FieldOperand[] = [];

    protected visitField(field): void {
        this.fields.push(field);
    }

    public getFields(): FieldOperand[] {
        return this.fields;
    }
}

export class RemoveFieldStatementVisitor extends StatementVisitorBase {
    private alias: string;
    private fieldId: string;

    constructor(fieldId: string, alias: string) {
        super();

        this.alias = alias;
        this.fieldId = fieldId;
    }

    protected visitMath(mathExpression): void {
        mathExpression.expressionElements = _(mathExpression.expressionElements)
            .filter(e => {
                if (e.$type !== FieldOperand.typeName) {
                    return true;
                }
                return !(e.tableAlias === this.alias && (this.fieldId === undefined || e.fieldId === this.fieldId));
            })
            .value();

        super.visitMath(mathExpression);
    }
}
